// 主数据接口的类型与访问函数。字段名与表列名相同，含义见 docs/04-管理接口.md 第 2 节、docs/03-核心表结构.md 第 3 节。
import { api } from "./api";
import type { Role } from "./auth";

export type EntityStatus = "active" | "disabled";
export type Protocol = "smpp" | "http";
/** 通道协议：比接入协议多一个 loopback，只用于黑洞通道（决策 D203）。 */
export type ChannelProtocol = Protocol | "loopback";
export type TrafficType = "otp" | "marketing";
export type ChargeOn = "submitted" | "delivered";

/** 只读列：服务端写入时忽略，提交前从整行里去掉。 */
const READONLY = ["id", "created_at", "updated_at", "version", "created_by"] as const;
type ReadonlyKey = (typeof READONLY)[number];

export interface Customer {
  id: number; name: string; currency: string; charge_on: ChargeOn; tier: string; status: EntityStatus; note: string | null;
  created_at: string; updated_at: string;
}

export interface Supplier {
  id: number; name: string; status: EntityStatus; note: string | null; created_at: string; updated_at: string;
  /** 系统预置的供应商，只读（决策 D203）。 */
  system?: boolean;
}

export interface Account {
  id: number; customer_id: number; name: string; protocol: Protocol; system_id: string;
  ip_whitelist: string[]; ip_unrestricted: boolean; tls_required: boolean; tps: number; max_binds: number;
  traffic_type: TrafficType; allowed_senders: string[]; credit_limit: number;
  pinned_channel_id: number | null; webhook_url: string | null; webhook_allow_http: boolean; status: EntityStatus;
  /** 客户回执策略，未绑定保持真实回执（D105）。 */
  dlr_policy_id?: number | null;
  created_at: string; updated_at: string;
}

export interface Channel {
  id: number; supplier_id: number; name: string; protocol: ChannelProtocol; status: EntityStatus; manually_removed: boolean;
  /** 黑洞通道：只能启停与摘除，不能删除、不配成本价（决策 D203）。 */
  system?: boolean;
  /** 协议参数的原样 JSON，结构由 connector 定义（决策 D28）。 */
  config: unknown; tls: unknown; error_map: unknown; circuit: unknown;
  adapter: string | null; dlr_source_ips: string[]; webhook_token: string | null; max_binds: number;
  tps: number; window_size: number; max_submit_retries: number; retry_backoff_ms: number[];
  dlr_supported: boolean; dlr_trusted: boolean; validity_period_supported: boolean; idempotency_key_supported: boolean;
  custom_sender_supported: boolean; unicode_supported: boolean; max_length: number;
  /** 通道支持的 sender，空表示不限；路由类型只做展示（决策 D61）。 */
  senders: string[]; route_type: string | null;
  created_at: string; updated_at: string;
}

/** 号码前缀表的一行，主键是 prefix；停用的前缀不参与匹配，号码回退到更短的前缀（docs/03 3.6）。 */
export interface NumberingPlan { prefix: string; country: string; mcc: string; mnc: string; operator_name: string; status: EntityStatus; updated_at: string }

export interface SellPrice {
  sync_task_id?: number;
  charge_on: ChargeOn;
  id: number; account_id: number; mcc: string; mnc: string | null; price: number; currency: string;
  effective_from: string; correction_of: number | null; created_by: string; created_at: string;
}

/** 成本价的可编辑时间线期间，对应表 cost_price（D113、docs/03 3.8）。 */
export interface CostPrice {
  sync_task_id?: number;
  charge_on: ChargeOn;
  id: number; channel_id: number; mcc: string; mnc: string | null; price: number; currency: string;
  effective_from: string; correction_of: number | null; created_by: string; created_at: string;
}

/** 币种到基准币种的汇率版本，对应表 currency_rate（docs/03 3.7）：1 原币 = rate / 1e6 基准币，只增不改（决策 D5）。 */
export interface CurrencyRate { id: number; currency: string; rate: number; effective_from: string; created_by: string; created_at: string }

/** 通道组的一个成员（docs/03 4.1，决策 D69）：通道或另一个通道组，二选一；time 策略的成员带时段（北京时间）。 */
export interface GroupMember {
  channel_id: number | null; group_id: number | null; priority: number; weight: number; time_start: string | null; time_end: string | null;
}

/** 通道组，成员随组一起读写，PUT 整体替换并把 version 加一。选路策略与参数在组上（决策 D69，docs/10 第 3 节）。 */
export interface ChannelGroup {
  id: number; name: string; status: EntityStatus; note: string | null; version: number; created_at: string; updated_at: string;
  strategy: string; params: unknown; members: GroupMember[];
}

/** 实体的写入体：整行去掉只读列。 */
export type Input<T> = Omit<T, ReadonlyKey>;
/** 账号的写入体比表多 password：新建必填，长度按协议（SMPP 6 到 8 个 ASCII 字符、HTTP 至少 8 个字符，D162），更新留空表示不改。 */
export type AccountInput = Input<Account> & { password: string };

/** 管理后台的后台账号（docs/04 第 2、9 节，决策 D43、D181）：只有超级管理员能读写，响应不含密码哈希。 */
export interface AdminUser { id: number; username: string; role: Role; status: EntityStatus; created_at: string; updated_at: string; last_login_at: string | null }
/** 后台账号的写入体：password 新建必填、满足复杂度规则（app/password.ts，D201），更新时留空表示不改。 */
export type AdminUserInput = Omit<Input<AdminUser>, "last_login_at"> & { password: string };

/** 把一行变成可以原样 PUT 回去的写入体。 */
export function editable<T extends object>(row: T): Input<T> {
  const out = { ...row } as Record<string, unknown>;
  for (const k of READONLY) delete out[k];
  return out as Input<T>;
}

export interface Resource<T, In = Input<T>> {
  list(query?: Record<string, string>): Promise<T[]>;
  /** 读一行，标识是 id，号码前缀表是 prefix。 */
  get(key: number | string): Promise<T>;
  create(body: In): Promise<T>;
  /** 标识是 id，号码前缀表是 prefix。 */
  update(key: number | string, body: In): Promise<T>;
}

/** 一种实体的列表、新建与整行更新，对应 docs/04 第 2 节的路径约定。 */
export function resource<T, In = Input<T>>(path: string): Resource<T, In> {
  return {
    list: (query) => api<T[]>(query && Object.keys(query).length ? `${path}?${new URLSearchParams(query)}` : path),
    get: (key) => api<T>(`${path}/${key}`),
    create: (body) => api<T>(path, { method: "POST", body: JSON.stringify(body) }),
    update: (key, body) => api<T>(`${path}/${key}`, { method: "PUT", body: JSON.stringify(body) }),
  };
}

/** 删除前的检查（docs/04 2.2，决策 D180）：blockers 为空才能删，cascades 是会一并删除的从属配置及条数。 */
export interface DeleteCheck { blockers: string[]; cascades: string[] }

/** 误建实体的删除检查与删除，标识同 Resource。 */
export interface Removable {
  deleteCheck(key: number | string): Promise<DeleteCheck>;
  remove(key: number | string): Promise<void>;
}

/** 可以删除的实体：列表、新建、整行更新，加上删除检查与删除（D180）。 */
export function removable<T, In = Input<T>>(path: string): Resource<T, In> & Removable {
  return {
    ...resource<T, In>(path),
    deleteCheck: (key) => api<DeleteCheck>(`${path}/${key}/delete-check`),
    remove: (key) => api<void>(`${path}/${key}`, { method: "DELETE" }),
  };
}

export const customers = removable<Customer>("/customers");
export const suppliers = removable<Supplier>("/suppliers");
export const accounts = removable<Account, AccountInput>("/accounts");
export const channels = removable<Channel>("/channels");

export const prefixes = removable<NumberingPlan>("/prefixes");

/** 号码前缀导入的结果（#138）：apply 为假只预览；有错误行时接口回 400，结果在 ApiError.body 里，整批不写。 */
export interface PrefixImportResult { rows: number; created: number; updated: number; unchanged: number; errors: { line: number; message: string }[] | null }

/** 导入号码前缀：csv 是文件全文，表头固定为 prefix,country,mcc,mnc,operator_name。 */
export const importPrefixes = (csv: string, apply: boolean) => api<PrefixImportResult>("/prefixes/import", { method: "POST", body: JSON.stringify({ csv, apply }) });
export const sellPrices = resource<SellPrice>("/sell-prices");
export const costPrices = resource<CostPrice>("/cost-prices");
export const currencyRates = resource<CurrencyRate>("/currency-rates");
export const adminUsers = removable<AdminUser, AdminUserInput>("/admin-users");
export const channelGroups = removable<ChannelGroup>("/channel-groups");

/** 利润保护规则（决策 D70，docs/04 第 2 节）：维度建好后不改；net_profit、counted_since、tripped_at 由累计与重置维护，写入时忽略。 */
export interface ProfitGuard {
  id: number; account_id: number; mcc: string; mnc: string | null;
  /** 允许的最大净亏损，基准币种微单位。 */
  loss_limit: number;
  /** 统计起点之后的累计净利润，负数是亏损。 */
  net_profit: number; counted_since: string; tripped_at: string | null;
  status: EntityStatus; note: string | null; created_at: string; updated_at: string;
}
export type ProfitGuardInput = Pick<ProfitGuard, "account_id" | "mcc" | "mnc" | "loss_limit" | "status" | "note">;

export const profitGuards = { ...removable<ProfitGuard, ProfitGuardInput>("/profit-guards"),
  /** 手工重置：累计清零、从现在重新统计、解除熔断。 */
  reset: (id: number) => api<ProfitGuard>(`/profit-guards/${id}/reset`, { method: "POST" }) };

/** 告警阈值的规则（决策 D190、D191）：通道送达率、账号送达率、通道排队时长、余额告警线、通道与账号的连续失败。 */
export type AlertRule = "delivery_rate" | "account_delivery_rate" | "channel_queue_wait" | "balance_low" | "channel_consecutive_failures" | "account_consecutive_failures";
/** 比较方式：absolute 直接比，baseline 低于自身 7 天均值的比例（只用于两个送达率），off 不告警。 */
export type AlertMode = "absolute" | "baseline" | "off";

/** 告警阈值（决策 D190，docs/04 第 2 节）：作用对象全空的是规则的全局默认；作用对象建好后不改，编辑只改 mode、value、note。 */
export interface AlertThreshold {
  id: number; rule: AlertRule; channel_id: number | null; account_id: number | null; mcc: string | null; mnc: string | null;
  mode: AlertMode;
  /** 单位随规则：送达率万分比、排队秒数、余额是账号币种微单位。 */
  value: number;
  note: string | null; created_at: string; updated_at: string;
}
export type AlertThresholdInput = Pick<AlertThreshold, "rule" | "channel_id" | "account_id" | "mcc" | "mnc" | "mode" | "value" | "note">;
export const alertThresholds = removable<AlertThreshold, AlertThresholdInput>("/alert-thresholds");

/** SID 替换策略（决策 D154，docs/04 第 2 节）：条件为空表示不限；先精确映射（不区分大小写），再按顺序试正则（整串匹配），都没命中才用默认替换。 */
export interface SenderMapping { from: string; to: string }
export interface SenderPattern { pattern: string; to: string }
export interface SenderPolicy {
  id: number; name: string;
  /** 数字越小越优先，全表唯一；只取第一条条件全部满足的策略。 */
  priority: number;
  account_ids: number[]; channel_ids: number[]; mcc: string; mnc: string;
  mappings: SenderMapping[]; patterns: SenderPattern[];
  /** 默认替换：所有 sender 都换成 default_target，目标可以为空。 */
  default_enabled: boolean; default_target: string;
  status: EntityStatus; note: string | null; version: number; created_at: string; updated_at: string;
}
export type SenderPolicyInput = Omit<SenderPolicy, ReadonlyKey>;
export const senderPolicies = removable<SenderPolicy, SenderPolicyInput>("/sender-policies");

/** sender 的展示：空 sender（SID 替换的空目标）写明"空"，免得看起来像没填。 */
export function senderLabel(s: string): string {
  return s === "" ? "（空）" : s;
}

// 路由策略的展示文案（架构说明 8.6）；注册表里没有的策略按名称原样显示。
export const STRATEGY: Record<string, string> = { priority: "静态优先级", weighted: "按权重", least_cost: "最低成本", quality_based: "按质量", time: "按时间", retry: "失败重试", number_affinity: "按号码历史", primary_backup: "主备" };

// 枚举的展示文案，取值见 docs/03 第 2 节。
export const PROTOCOL: Record<Protocol, string> = { smpp: "SMPP", http: "HTTP" };
/** 通道在界面上的类型：HTTP 通道按适配器区分，目前只有 Telegram（D207）；适配器认不出的旧 HTTP 通道仍叫 HTTP（#405）。 */
export type ChannelKind = "smpp" | "telegram" | "http" | "loopback";
export const CHANNEL_KIND: Record<ChannelKind, string> = { smpp: "SMPP", telegram: "Telegram", http: "HTTP", loopback: "黑洞" };
export function channelKind(c: Pick<Channel, "protocol" | "adapter">): ChannelKind {
  return c.protocol === "http" && c.adapter === "telegram" ? "telegram" : c.protocol;
}
export const TRAFFIC_TYPE: Record<TrafficType, string> = { otp: "OTP", marketing: "营销" };
export const CHARGE_ON: Record<ChargeOn, string> = { submitted: "按提交计费", delivered: "按送达计费" };
export const ENTITY_STATUS: Record<EntityStatus, string> = { active: "启用", disabled: "停用" };

/** 枚举文案表转成 Select 或 RadioGroup 的选项。 */
export function toOptions<K extends string>(labels: Record<K, string>): { value: K; label: string }[] {
  return (Object.keys(labels) as K[]).map((value) => ({ value, label: labels[value] }));
}

/** 客户回执策略，比例用基点，版本由服务端递增（D105）。 */
export interface DLRPolicy { id: number; name: string; timeout_sec: number; failure_success_bps: number; version: number; status: EntityStatus }
export type DLRPolicyInput = Omit<DLRPolicy, "id" | "version">;
export const dlrPolicies = removable<DLRPolicy, DLRPolicyInput>("/dlr-policies");
